import tradevuLogo from "../assets/images/tradevulogo2.svg";

import { forwardRef } from "react";

import { formatMoney as formatM } from "../components/formatCurrency";

import Avatar from "../components/Avatar";
import { getInitials } from "../components/getInitials";
import capitalizeWords from "../components/capitaliseWords";
import { XIcon } from "lucide-react";
import Modal from "react-modal";

const Preview = forwardRef(
    (
        {
            currency,
            user,
            supplier,
            dueDate,
            orderItems,
            subtotal,
            surCharge,
            addTax,
            total,
            paymentTerms,
            splitTerm,
            note,
            preview,
            onClose,
            setShowPreview
        },
        ref
    ) => {
        const formatMoney = (money, compact, len) =>
            formatM(money, compact, len).replace(/,/g, " ");
        const customStyles = {
            content: {
                margin: "5% auto",
                overflow: "hidden",
                maxWidth: "400px",
                height: "380px",
                width: "85%",
                padding: "20px",
                borderRadius: "8px",
            },
            overlay: {
                backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
        };
        const gridStyle =
            "grid grid-cols-[1fr,55px,minmax(100px,120px),minmax(100px,115px)]";
        return (
            <Modal
                isOpen={preview}
                onRequestClose={onClose}
                style={customStyles}
                contentLabel="Preview Modal"
            >
                <div
                    id="preview-pdf"
                    className={`bg-white rounded-lg shadow-lg transition-all duration-300
                    fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
                    ${preview ? "z-40 opacity-100" : "opacity-0 -z-[999]"}
                    w-[90%] max-w-3xl h-[95vh] overflow-y-auto`}
                    ref={ref}
                >
                    <h3 className="flex items-center font-[430] text-[19px] !py-[18px] px-[30px] !border-b-4 border-[#d9cdff] relative">
                        <img src={tradevuLogo} alt="Tradevu" className=" mr-auto" />
                        <div className="flex items-center gap-3 justify-end">
                            PURCHASE ORDER
                            {onClose && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onClose?.()
                                    }}
                                    className="text-sm text-gray-500 hover:text-gray-800"
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        fontSize: "30px",
                                        fontWeight: "bold",
                                    }}
                                >
                                    <XIcon />
                                </button>
                            )}
                        </div>
                    </h3>

                    <div className="grid grid-cols-[43%,1fr] gap-4 px-[30px] mt-[30px]">
                        <div className="flex">
                            <Avatar
                                className={`font-[430] size-[42px] ${getInitials(user?.bizName ?? "t v").length < 4
                                    ? "text-[17px]"
                                    : "text-sm"
                                    }`}
                                fullName={user?.bizName ?? "t v"}
                                img={user?.bizImg ?? ""}
                            />
                            <p className="text-sm font-medium">
                                {capitalizeWords(user?.bizName ?? "")}
                                <span className="block font-normal text-[13px] text-main-grey !mt-1">
                                    {capitalizeWords(user?.fullName ?? "")}
                                </span>
                            </p>
                        </div>

                        {supplier && (
                            <p className="text-sm font-[470]">
                                To
                                <span className="block mt-[7px] mb-[5px]">
                                    {capitalizeWords(supplier.name)}
                                </span>
                                <span className="block font-normal text-main-grey">
                                    {capitalizeWords(supplier.personalName)}
                                </span>
                            </p>
                        )}
                    </div>

                    <p className="px-[30px] text-main text-sm !mt-[26px] !mb-[42px]">
                        Due: {dueDate}
                    </p>

                    {/* Items Section */}
                    <div>
                        <div
                            className={`${gridStyle} mx-[30px] !pb-[13px] !mb-[9px] text-sm font-normal text-main-grey border-b *:!bg-transparent`}
                        >
                            <span>Items</span>
                            <span className="translate-x-3">Quantity</span>
                            <span className="text-right">Unit Price</span>
                            <span className="text-right">Total</span>
                        </div>

                        {orderItems?.map(({ id, itemName, pricePerUnit, quantity }) => (
                            <p
                                className={`${gridStyle} px-[30px] text-main font-normal text-[15px] !py-2`}
                                key={id}
                            >
                                <span>{itemName}</span>
                                <span className="translate-x-6">{formatMoney(quantity)}</span>
                                <span className="text-right">
                                    {currency}
                                    {formatMoney(pricePerUnit, true)}
                                </span>
                                <span className="text-right">
                                    {currency}
                                    {formatMoney(pricePerUnit * quantity, true)}
                                </span>
                            </p>
                        ))}

                        <div className="grid grid-cols-[47.5%,52.5%] mx-[30px] border-t !mt-1.5">
                            <span />
                            <p className="flex items-center justify-end font-normal !py-[18px] text-[15px] text-main">
                                <span className="mr-auto text-sm text-main-grey">Subtotal</span>
                                {currency}
                                {formatMoney(Number(subtotal).toFixed(2))}
                            </p>
                        </div>

                        <div className="grid grid-cols-[47.5%,52.5%] mx-[30px] border-t">
                            <span />
                            <p className="flex items-center justify-end font-normal !py-[18px] text-[15px] text-main">
                                <span className="mr-auto text-sm text-main-grey">Surcharge</span>
                                {currency}
                                {formatMoney(Number(surCharge).toFixed(2))}
                            </p>
                        </div>

                        <div className="grid grid-cols-[47.5%,52.5%] mx-[30px] border-t">
                            <span />
                            <p className="flex items-center justify-end font-normal !py-[18px] text-[15px] text-main">
                                <span className="mr-auto text-sm text-main-grey">Tax (7.5%)</span>
                                {currency}
                                {formatMoney(Number(addTax).toFixed(2))}
                            </p>
                        </div>

                        <div className="grid grid-cols-[47.5%,52.5%] mx-[30px] border-t">
                            <span />
                            <p className="flex items-center justify-end !py-[18px] text-[15px] font-semibold">
                                <span className="mr-auto text-sm font-normal text-main-grey">
                                    Amount due
                                </span>
                                {currency}
                                {formatMoney(Number(total).toFixed(2))}
                            </p>
                        </div>
                    </div>

                    {/* Payment & Notes */}
                    <div className="mx-[30px] !border !border-[#f5f6fd] rounded-lg !px-4 py-3.5 !mt-4">
                        <p className="text-[13px] text-main-grey font-normal">
                            Payment terms
                        </p>
                        <p className="text-sm font-medium !mt-1">{paymentTerms}</p>
                        {paymentTerms?.startsWith("Split") && (
                            <p className="font-normal text-[13px] text-main !mt-1.5">
                                {splitTerm}
                            </p>
                        )}
                        {note && (
                            <>
                                <p className="text-[13px] text-main-grey font-normal !mt-5">
                                    Notes
                                </p>
                                <p className="text-sm font-medium !mt-1">{note}</p>
                            </>
                        )}
                    </div>

                    <p className="text-[#ab6bff] mx-[30px] font-[375] !mt-8 !mb-1.5 !text-left">
                        Thank you!
                    </p>
                    <p className="text-xs leading-[17px] text-main-grey font-[375] mx-[30px] !pb-4 !text-left">
                        If you have questions for us, please call a representative on{" "}
                        <a
                            href="tel:+2347073183321"
                            className="!text-[#6F00FF] hover:underline"
                        >
                            +2347073183321
                        </a>{" "}
                        or{" "}
                        <a
                            href="tel:+447903175084"
                            className="!text-[#6F00FF] hover:underline"
                        >
                            +447903175084
                        </a>
                        . You can also send us an email at{" "}
                        <a
                            href="mailto:hello@tradevu.co"
                            className="!text-[#6F00FF] hover:underline"
                        >
                            hello@tradevu.co
                        </a>
                        . Thank you for using Tradevu.
                    </p>
                </div>
            </Modal>
        );
    }
);

export default Preview;
